/* eslint-disable @typescript-eslint/no-explicit-any */
// @ts-nocheck
/**
 * TierLegend — pinned color strip showing the four engagement tiers and their
 * score thresholds. Pin it under the header of any view that talks about
 * tiers so the vocabulary is always one glance away.
 *
 * Two layouts:
 *  - default: full strip with all four tiers + a (?) info icon at the end
 *  - compact: small inline pill with just the colors and an info icon, useful
 *    inside table headers and tight cards
 *
 * Each tier swatch is itself an InfoTip linked to the matching tier glossary
 * entry, so hovering "Hot" in the strip pops the Hot definition.
 */
import React from 'react';
import { TIER_PALETTE } from '@/lib/glossary';
import { InfoTip } from './InfoTip';

const TIERS = [
  { id: 'anchor_tier', label: 'Anchor', range: TIER_PALETTE.anchor.range, color: TIER_PALETTE.anchor.hex, dotClass: 'bg-tier-anchor', borderClass: 'border-tier-anchor' },
  { id: 'hot_tier', label: 'Hot', range: TIER_PALETTE.hot.range, color: TIER_PALETTE.hot.hex, dotClass: 'bg-tier-hot', borderClass: 'border-tier-hot' },
  { id: 'warm_tier', label: 'Warm', range: TIER_PALETTE.warm.range, color: TIER_PALETTE.warm.hex, dotClass: 'bg-tier-warm', borderClass: 'border-tier-warm' },
  { id: 'cool_tier', label: 'Cool', range: TIER_PALETTE.cool.range, color: TIER_PALETTE.cool.hex, dotClass: 'bg-tier-cool', borderClass: 'border-tier-cool' },
] as const;

type Props = {
  /** Compact = small inline pill. Default = full strip. */
  compact?: boolean;
  /** Optional className override. */
  className?: string;
};

export const TierLegend: React.FC<Props> = ({ compact = false, className = '' }) => {
  if (compact) {
    return (
      <span className={`inline-flex items-center gap-1 ${className}`}>
        {TIERS.map((t) => (
          <span
            key={t.id}
            className={`inline-block w-2.5 h-2.5 rounded-sm ${t.dotClass}`}
            title={`${t.label} (${t.range})`}
          />
        ))}
        <InfoTip termId="tier_overview" compact />
      </span>
    );
  }

  return (
    <div
      className={`flex items-center gap-2 flex-wrap rounded-lg border border-border-base bg-bg-raised/60 px-3 py-2 ${className}`}
    >
      <span className="text-[10px] uppercase tracking-wide text-fg-muted font-semibold mr-1">
        Engagement Tiers
      </span>
      {TIERS.map((t) => (
        <InfoTip key={t.id} termId={t.id} showIcon={false}>
          <span
            className={`inline-flex items-center gap-1.5 rounded-md border border-border-base bg-bg-base px-2 py-1 text-[11px] cursor-help hover:border-border-subtle transition border-l-[3px] ${t.borderClass}`}
          >
            <span
              className={`inline-block w-2 h-2 rounded-sm ${t.dotClass}`}
            />
            <span className="font-semibold text-fg-primary">{t.label}</span>
            <span className="text-fg-muted font-mono text-[10px]">{t.range}</span>
          </span>
        </InfoTip>
      ))}
      <InfoTip termId="tier_overview" />
    </div>
  );
};

export default TierLegend;
